import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Typography from '../components/v2/Typography';
import Button from '../components/v2/Button';
import Input from '../components/v2/Input';
import { Download, ArrowRight, Search, Menu, SearchIcon, Globe, InfoIcon, CheckIcon, Cross, X, ChevronDown } from 'lucide-react';
import IButton from '../components/v2/IButton';
import { motion, AnimatePresence } from 'framer-motion';
const TITLE_MAP = {
    "/services/domain-acquisition": "Domain Acquisition Service",
    "/services/domain-brokering": "Domain Brokering Service",
    "/domains-for-sale": "Domains For Sale",
    "/services/consulting": "Consultation Service",
    "/services/portfolio-management": "Portfolio Management Service",
    "/services/discreet-acquisitions": "Discreet Acquisitions Service",
    "/services/marketing-design": "Marketing & Design Service",
    "/tool/appraisal": "Domain Appraisal Tool",
    "/tool/whois": "WHOIS Lookup Tool"
};

const SEARCH_TERMS = {
    "/services/domain-brokering": ["domain broker", "domain brokering", "domain buying", "domain selling", "service", "services"],
    "/domains-for-sale": ["domain for sale", "domain buying", "domain selling", "service", "services"],
    "/services/consulting": ["consulting", "consultation", "domain advice", "expert help", "service", "services"],
    "/services/portfolio-management": ["portfolio", "domain management", "portfolio strategy", "service", "services"],
    "/services/discreet-acquisitions": ["private domain buy", "discreet acquisition", "secret domain purchase", "service", "services"],
    "/services/marketing-design": ["branding", "design", "marketing", "domain promotion", "service", "services"],
    "/tool/appraisal": ["valuation", "domain appraisal", "appraisal tool", "estimate value", "tools", "tool"],
    "/tool/whois": ["whois", "domain owner", "lookup", "whois tool", "tools", "tool"]
};

const servicesDropdownData = [
    { title: "Domain Brokering", href: "/services/domain-brokering", description: "Professional domain acquisition and sales" },
    { title: "Domain Acquisition", href: "/services/domain-acquisition", description: "Secure your perfect domain name" },
    { title: "Consulting", href: "/services/consulting", description: "Expert domain strategy advice" },
    { title: "Strategy", href: "/services/strategy", description: "Maximize your domain investments" },
    // { title: "Discreet Acquisitions", href: "/services/discreet-acquisitions", description: "Confidential domain purchases" },
    { title: "Marketing & Design", href: "/services/marketing-design", description: "Brand and promote your domains" }
];

const Header = () => {

    const headerRef = useRef(null);
    const [isFixed, setIsFixed] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isServicesOpen, setIsServicesOpen] = useState(false);
    const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
    const servicesRef = useRef(null);
    const servicesTimeoutRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!headerRef.current) return;

            const headerTop = headerRef.current.offsetTop;
            const scrollY = window.scrollY;

            if (scrollY > headerTop) {
                setIsFixed(true);
            } else {
                setIsFixed(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleOpenMenu = (e) => {
        setIsMenuOpen(!isMenuOpen);
    }

    const handleCloseMenu = (e) => {
        setIsMenuOpen(false);
    }

    const handleSearchToggle = () => {
        setIsSearchOpen(!isSearchOpen);
        if (!isSearchOpen) {
            setSearchQuery('');
        }
    }

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    }

    const exampleResults = [
        { title: "example.com", price: "$15,000", category: "Tech" },
        { title: "mybusiness.com", price: "$8,500", category: "Business" },
        { title: "startup.io", price: "$12,000", category: "Startup" },
        { title: "portfolio.net", price: "$5,200", category: "Portfolio" }
    ];

    useEffect(() => {
        if (searchQuery?.trim().length > 0) {
            const query = searchQuery.toLowerCase();
            const matchedLinks = Object.entries(SEARCH_TERMS).filter(([_, terms]) =>
                terms.some(term => term.toLowerCase().includes(query))
            ).map(([key]) => ({
                title: TITLE_MAP[key],
                link: key
            }));
            setSearchResults(matchedLinks);
        } else {
            setSearchResults([]);
        }
    }, [searchQuery]);

    const handleServicesMouseEnter = () => {
        clearTimeout(servicesTimeoutRef.current);
        setIsServicesOpen(true);
    };

    const handleServicesMouseLeave = () => {
        servicesTimeoutRef.current = setTimeout(() => {
            setIsServicesOpen(false);
        }, 300);
    };

    const handleMobileServicesToggle = () => {
        setIsMobileServicesOpen(!isMobileServicesOpen);
    };
    

    return (
        <>
            <header ref={headerRef}
                className={`main-header ${isFixed ? "fixed-header" : ""}`}>

                <div className="inner">
                    <div className="section-left">
                        <div className="logo-section">
                            <a href="/">
                                <img src="https://cdn.webstadia.com/98c2d7d9-2537-4964-9f2b-025d42e7b076.png" alt="Logo" />
                            </a>
                            {/* <span className="logo-text">NameConnect</span> */}
                        </div>
                        <div className="links-section">
                            <ul>
                                <li><a href="/domains-for-sale"><span className="nav-text active">Domains For Sale</span></a></li>
                                <li><a href="/about"><span className="nav-text active">About</span></a></li>
                                <li 
                                    className="services-dropdown-container"
                                    onMouseEnter={handleServicesMouseEnter}
                                    onMouseLeave={handleServicesMouseLeave}
                                    ref={servicesRef}
                                >
                                    <a href="/services" className="services-link">
                                        <span className="nav-text">Services</span>
                                        <ChevronDown size={16} className="dropdown-icon" />
                                    </a>
                                    <AnimatePresence>
                                        {isServicesOpen && (
                                            <motion.div
                                                className="services-dropdown"
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <div className="dropdown-inner">
                                                    {servicesDropdownData.map((service, index) => (
                                                        <Link 
                                                            key={index} 
                                                            to={service.href}
                                                            className="dropdown-item"
                                                            onClick={() => setIsServicesOpen(false)}
                                                        >
                                                            <div className="dropdown-item-content">
                                                                <Typography variant="h4">{service.title}</Typography>
                                                                <Typography variant="para">{service.description}</Typography>
                                                            </div>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </li>
                                {/* <li><a href="/tool"><span className="nav-text">Tools</span></a></li> */}
                                <li><a href="/blog"><span className="nav-text">Blog</span></a></li>
                                <li><a href="/resources"><span className="nav-text">Resources</span></a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="section-right">
                        <div className="section-left">
                            <ul>
                                <li className='hide-mobile'><a href="tel:+19415009689"><span className="nav-text">+1 (941) 500-9689</span></a></li>
                                <li>
                                    <IButton onClick={handleSearchToggle} icon={Search} variant='secondary' />
                                </li>
                            </ul>
                        </div>
                        <div className="section-right">
                            <ul>
                                <li className='hide-mobile'>
                                    <Button variant='primary-nav' isLink={true} linkProps={{to:"/tool/appraisal"}}>
                                        <Typography variant={'button'}>Get Valuation</Typography>
                                    </Button>
                                </li>
                                <li className="mobile-only">
                                    <IButton onClick={handleOpenMenu} icon={Menu} variant='primary' />
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>

            {/* {isMenuOpen && <div className="bg-mobile-menu">
            <div className="mobile-menu">
                <div className="inner">
                    <ul>
                        <li className='close-iconn'>
                            <IButton onClick={handleCloseMenu} icon={X} variant='primary' />
                        </li>
                        <li><a href="/featured"><span className="nav-text active">Featured</span></a></li>
                        <li><a href="/tool"><span className="nav-text">Tools</span></a></li>
                        <li><a href="/services"><span className="nav-text">Services</span></a></li>
                        <li><a href="/tool"><span className="nav-text">Resources</span></a></li>
                        <li><a href="tel:+19415009689"><span className="nav-text">Call Us +1 (941) 500-9689</span></a></li>
                    </ul>
                </div>
            </div>
        </div>} */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        className="bg-mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <motion.div
                            className="mobile-menu"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="inner">
                                <ul>
                                    <li className='close-iconn'>
                                        <IButton onClick={handleCloseMenu} icon={X} variant='primary' />
                                    </li>
                                    <li><a href="/domains-for-sale"><span className="nav-text active">Domains For Sale</span></a></li>
                                    <li><a href="/about"><span className="nav-text">About</span></a></li>
                                    <li className="mobile-services-dropdown">
                                        <div 
                                            className="mobile-services-toggle"
                                            onClick={handleMobileServicesToggle}
                                        >
                                            <span className="nav-text">Services</span>
                                            <ChevronDown 
                                                size={20} 
                                                className={`dropdown-icon ${isMobileServicesOpen ? 'open' : ''}`} 
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {isMobileServicesOpen && (
                                                <motion.div
                                                    className="mobile-dropdown-items"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    {servicesDropdownData.map((service, index) => (
                                                        <a 
                                                            key={index} 
                                                            href={service.href}
                                                            className="mobile-dropdown-item"
                                                            onClick={handleCloseMenu}
                                                        >
                                                            <span className="nav-text sub">{service.title}</span>
                                                        </a>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </li>
                                    <li><a href="/blog"><span className="nav-text">Blog</span></a></li>
                                    <li><a href="/resources"><span className="nav-text">Resources</span></a></li>
                                    <li><a href="tel:+19415009689"><span className="nav-text">Call Us +1 (941) 500-9689</span></a></li>
                                </ul>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Search Modal */}
            <AnimatePresence>
                {isSearchOpen && (
                    <motion.div
                        className="search-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={handleSearchToggle}
                    >
                        <motion.div
                            className="search-modal"
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="search-header">
                                <div className="search-input-container">
                                    <Search className="search-icon" size={20} />
                                    <input
                                        type="text"
                                        placeholder="Search domains..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        className="search-input"
                                        autoFocus
                                    />
                                    <button onClick={handleSearchToggle} className="search-close">
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>
                            
                            {searchQuery && (
                                <div className="search-results">
                                    <div className="search-results-header">
                                        <Typography variant="body-sm">Search Results</Typography>
                                    </div>
                                    <div className="search-results-list">
                                        {searchResults
                                            .filter(result => 
                                                result.title.toLowerCase().includes(searchQuery.toLowerCase())
                                            )
                                            .map((result, index) => (
                                                <div key={index} className="search-result-item">
                                                    <div className="result-info">
                                                        <div className="result-title">{result.title}</div>
                                                        <div className="result-category">{result.category}</div>
                                                    </div>
                                                    <div className="result-price">{result.price}</div>
                                                </div>
                                            ))
                                        }
                                        {searchResults.filter(result => 
                                            result.title.toLowerCase().includes(searchQuery.toLowerCase())
                                        ).length === 0 && (
                                            <div className="no-results">
                                                <Typography variant="body-sm">No results found matching "{searchQuery}"</Typography>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {!searchQuery && (
                                <div className="search-suggestions">
                                    <div className="suggestions-header">
                                        <Typography variant="body-sm">Popular Searches</Typography>
                                    </div>
                                    <div className="suggestions-list">
                                        {["Brokering service", "Domain valuation", "WHOIS info"].map((suggestion, index) => (
                                            <div 
                                                key={index} 
                                                className="suggestion-item"
                                                onClick={() => setSearchQuery(suggestion)}
                                            >
                                                <Search size={16} />
                                                <span>{suggestion}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>


        </>
    );
}


export default Header;